import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Menu, ConfigProvider, Popover, Typography, Divider, Modal, Alert } from 'antd';
import styles from './index.less';
import AbfMenuUtils from "@alife/abf-menu-utils";
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import { micorInit } from "@ali/ysera-wei";
import { InfoCircleOutlined, CaretDownOutlined, ExportOutlined } from '@ant-design/icons';
const { Text } = Typography;
const { Header, Content, Sider } = Layout;
import _ from "lodash";
import './hsf2http';
import vmsService from "./vmsService";
import LoginMod from './loginMod';
import menuData, { MenuItem, LoginInfo } from './menu';
import { InitAEM } from './aemTracker';
import { GlobalAlert } from "@alife/ads-components";

// 常量定义
const APP_ROOT_CONTAINER = "app-root-container";
const HOME_PATH = '/';
const INDEX_PATH = '/index';
const REGISTER_PATH = '/register';
const ACCOUNT_INFO_PATH = '/accountInfo';

export interface IAdsLayoutProps {
  menus?: MenuItem[];
  containorId?: string;
  pageVars?: any;
}

// 工具函数
const findLinkInTree = (tree: MenuItem[], targetLink: string): boolean => {
  return tree.some(node => {
    if (node.link === targetLink) return true;
    return node.children ? findLinkInTree(node.children, targetLink) : false;
  });
};

// 过滤权限菜单相关
const filterTreeByAuthCode = (tree: MenuItem[], role: string): MenuItem[] => {
  return tree
    .map((node) => {
      const newNode = { ...node };
      if ((newNode.authCode?.includes(role as any) || newNode.authCode?.length === 0) && 
          (!newNode?.grey || newNode?.grey?.((window as any).loginInfo))) {
        newNode.children = newNode.children ? filterTreeByAuthCode(newNode.children, role) : [];
        return newNode;
      } else if (newNode.children) {
        const filteredChildren = filterTreeByAuthCode(newNode.children, role);
        return filteredChildren.length > 0 ? { ...newNode, children: filteredChildren } : null;
      }
      return null;
    })
    .filter(Boolean) as MenuItem[];
};

const AdsLayout: React.FC<IAdsLayoutProps> = ({ containorId = APP_ROOT_CONTAINER }) => {
  // 状态管理
  const [menus, setMenus] = useState<MenuItem[]>(menuData);
  const [currentkey, setCurrentkey] = useState('');
  const [currentTopKey, setCurrentTopKey] = useState('');
  const [account, _setAccount] = useState<LoginInfo>();
  const [subMenuList, setSubMenuList] = useState<MenuItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [realSideMenu, setRealSideMenu] = useState<any[]>([]);
  const [sideOpenKeys, setSideOpenKeys] = useState<string[]>([]);
  
  const { handleNavChange, microJump } = AbfMenuUtils({});
  const adsAEM = useRef<any>(null);
  
  const cur_path = location.pathname;
  const subViewAdvertiserId = new URLSearchParams(window.location.search).get('subViewAdvertiserId');

  const setAccount = (params) => {    
    _setAccount(params);
    if (!adsAEM.current) {
      return
    }
    if (params?.moziId) {
      adsAEM.current?.setConfig({
        uid: params.moziId,
        username: params.advertiserName,
        advertiserId: params.advertiserId,
      });
    }else{
      adsAEM.current?.setConfig({
        uid: 'LoginServerError',
      });
    }
  }

  // 登录检查和跳转
  const microJumpWithLoginCheck = useCallback(async (url: string) => {
    try {
      const data = await vmsService.loginOrRegist();
      if (!data?.success && (data?.msgCode === 'MOZI_NOT_LOGIN' || data?.msgCode === 'MOZI_ACCOUNT_DELETED')) {
        setAccount({});
        if (cur_path !== HOME_PATH) {
          window.location.href = HOME_PATH;
        }
        return;
      }
      
      if (account?.advertiserId === data?.result?.advertiserId) {
        microJump(url);
      } else {
        window.location.href = HOME_PATH;
      }
    } catch (error) {
      console.error('Login check failed:', error);
      window.location.href = HOME_PATH;
    }
  }, [account?.advertiserId, cur_path]);

  // 查找侧边栏数据
  const findSideMenuData = useCallback((data: MenuItem[], key: string): MenuItem => {
    let sideData = {} as MenuItem;
    data?.forEach(item => {
      if (item.key === key) {
        sideData = item;
      } else if (item.children?.length) {
        const found = item.children.find(child => child.key === key);
        if (found) {
          sideData = item;
        } else {
          item.children.forEach(subItem => {
            const deepFound = subItem.children?.find(child => child.key === key);
            if (deepFound) {
              sideData = item;
            }
          });
        }
      }
    });
    return sideData;
  }, []);

  // 获取选中的菜单路径
  const getSelectedMenuPath = useCallback((menuItems: MenuItem[], targetKey: string): string[] => {
    const path: string[] = [];
    
    const findPath = (items: MenuItem[], key: string): boolean => {
      for (const item of items) {
        if (item.key === key) {
          path.push(item.key);
          return true;
        }
        if (item.children?.length) {
          if (findPath(item.children, key)) {
            path.unshift(item.key);
            return true;
          }
        }
      }
      return false;
    };

    findPath(menuItems, targetKey);
    return path;
  }, []);

  // 获取所有菜单的 keys
  const getAllMenuKeys = useCallback((menuItems: MenuItem[]): string[] => {
    const keys: string[] = [];
    const getKeys = (items: MenuItem[]) => {
      items.forEach(item => {
        if (item.key) {
          keys.push(item.key);
        }
        if (item.children?.length) {
          getKeys(item.children);
        }
      });
    };
    getKeys(menuItems);
    return keys;
  }, []);

  // 菜单选择处理
  const defaultSelect = useCallback((menuparams: MenuItem[]) => {
    if (!findLinkInTree(menuparams, location.pathname) && 
        ![HOME_PATH, REGISTER_PATH].includes(location.pathname)) {
      const loginInfo = (window as any)?.loginInfo;
      if (loginInfo?.role === 'worker_finance') {
        window.location.href = loginInfo?.openNewBp ? '/605ca278' : '/3cb45b5a';
      } else if (location.pathname !== INDEX_PATH) {
        window.location.href = INDEX_PATH;
      }
      return false;
    }

    const item = handleNavChange(menuparams, undefined, location.pathname);
    if (item) {
      const sideData = findSideMenuData(menuparams, item.key);
      const sideMenuChildren = sideData?.children || [];
      setSubMenuList(sideMenuChildren);
      setCurrentkey(item.key);
      // 设置所有菜单展开
      setSideOpenKeys(getAllMenuKeys(sideMenuChildren));
      setCurrentTopKey(sideData.key);
    }
  }, [findSideMenuData, getAllMenuKeys]);

  // 菜单点击处理
  const topMenuClick = useCallback(async (topItem: MenuItem) => {
    if (!topItem.isBlank) {
      await microJumpWithLoginCheck(topItem.link!);
    } else {
      window.open(topItem.link);
    }
    setCurrentTopKey(topItem.key);
    const k = (topItem.children?.[0]?.key) || topItem.key;
    setCurrentkey(k);
    setSubMenuList(topItem.children || []);
  }, [microJumpWithLoginCheck]);

  // 左侧菜单点击
  const leftMenuClick = useCallback(async (leftItem: MenuItem, callback?: () => void) => {
    if (!leftItem?.isBlank) {
      await microJumpWithLoginCheck(leftItem.link!);
      setCurrentkey(leftItem.key);
      callback?.();
    } else {
      window.open(leftItem.link);
    }
  }, [microJumpWithLoginCheck]);

  // 获取左侧菜单数据
  const getSideMenu = useCallback((data: MenuItem[]) => {
    return data?.map(item => {
      const { children, title, icon, key, greyTitle, showGreyTitle } = item;
      const visibleChildren = children?.filter(child => !child.hidden);
      
      if (visibleChildren?.length) {
        const formatData = visibleChildren.map(child => ({
          key: child.key,
          label: child.greyTitle && child.showGreyTitle?.((window as any).loginInfo) 
            ? child.greyTitle 
            : child.title,
          data: child,
        }));

        return {
          children: formatData,
          key,
          label: greyTitle && showGreyTitle?.((window as any).loginInfo) ? greyTitle : title,
          icon: icon?.(),
        };
      }
      
      return {
        key,
        label: greyTitle && showGreyTitle?.((window as any).loginInfo) ? greyTitle : title,
        data: { ...item, icon: icon?.() }
      };
    });
  }, []);

  // 登录处理
  const handleLogin = useCallback(async () => {
    try {
      const data = await vmsService.loginOrRegist();
      const res = data.result ?? {};
      
      if (!data?.success && ['MOZI_NOT_LOGIN', 'MOZI_ACCOUNT_DELETED'].includes(data?.msgCode)) {
        (window as any).pubsub.notify('isLogin', !!res?.advertiserId);
        setAccount({});
        if (cur_path !== HOME_PATH) {
          window.location.href = HOME_PATH;
        }
        return;
      }

      Object.assign(window, {
        loginInfo: res,
        isNewBp: res?.openNewBp,
        advertiserType: res?.type,
        advertiserId: res?.advertiserId
      });
      (window as any).pubsub.notify('isLogin', !!res?.advertiserId);
      setAccount(res);

      if (res.permissionsV2) {
        window.open('/v2/base/frontPage', '_self');
        return;
      }

      const { role } = res;
      let real_menu;

      if (!subViewAdvertiserId && !data.result.allShow) {
        if (location.pathname !== ACCOUNT_INFO_PATH) {
          window.location.href = ACCOUNT_INFO_PATH;
          return;
        }
        real_menu = filterTreeByAuthCode(menus, 'newaccount');
      } else {
        real_menu = filterTreeByAuthCode(menus, role);
      }

      setMenus(real_menu);
      handleRoleBasedRedirect(role, cur_path);
      
    } catch (error) {
      console.error('Login failed:', error);
      setAccount({});
    }
  }, [cur_path, menus]);

  // 角色基于重定向
  const handleRoleBasedRedirect = useCallback((role: string, currentPath: string) => {
    const roleRedirects: Record<string, () => void> = {
      sub: () => {
        if (currentPath !== HOME_PATH) window.location.href = HOME_PATH;
        setVisible(true);
      },
      agent: () => {
        (window as any).xrspermission = 'action';
        if (currentPath === HOME_PATH) window.location.href = INDEX_PATH;
      },
      worker_optimizer: () => {
        (window as any).xrspermission = 'action';
        if (currentPath === HOME_PATH) window.location.href = INDEX_PATH;
      },
      worker_saler: () => {
        (window as any).xrspermission = 'look';
        if (currentPath === HOME_PATH) window.location.href = INDEX_PATH;
      },
      worker_finance: () => {
        (window as any).xrspermission = 'action';
        if (currentPath === HOME_PATH) window.location.href = '/3cb45b5a';
      },
      worker_other: () => {
        (window as any).xrspermission = 'look';
        if (currentPath === HOME_PATH) window.location.href = INDEX_PATH;
      }
    };

    roleRedirects[role]?.();
  }, []);

  // 副作用
  useEffect(() => {
    if (account?.role) {
      const menu = filterTreeByAuthCode(_.cloneDeep(menuData), account.role);
      setMenus(menu);
      defaultSelect(menu);
    }
  }, [account?.role, defaultSelect]);

  useEffect(() => {
    adsAEM.current = new InitAEM();
    micorInit();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      console.log("浏览器的返回键被按下");
      defaultSelect(menus);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [defaultSelect, menus]);

  useEffect(() => {
    if (typeof (window as any).WEICONFIG === 'object') {
      (window as any).WEICONFIG = {
        config: (window as any).WEICONFIG,
        loading: true,
      };
    }
  }, []);

  useEffect(() => {
    sessionStorage.removeItem('ABF_STORAGE');
    if (location.pathname !== REGISTER_PATH) {
      handleLogin();
    } else {
      (window as any).pubsub.notify('isLogin', false);
      micorInit();
    }
  }, []);

  useEffect(() => {
    let sideMenuData = account?.role
      ? getSideMenu(filterTreeByAuthCode(subMenuList, account.role))
      : getSideMenu(subMenuList);

    setRealSideMenu(sideMenuData);
    // 初始化时设置所有菜单展开
    if (sideMenuData?.length) {
      const allKeys = getAllMenuKeys(subMenuList);
      setSideOpenKeys(allKeys);
    }
  }, [subMenuList, account?.role, getSideMenu, getAllMenuKeys]);

  // 顶部下拉菜单点击
  const onTopClick = useCallback((item: MenuItem, items: MenuItem[]) => {
    leftMenuClick(item, () => {
      if (items) {
        setSubMenuList(items);
      }
    });
  }, [leftMenuClick]);

  // 渲染顶部下拉菜单
  const renderMenuOnPop = useCallback((items: MenuItem[], father: MenuItem) => {
    const realItems = items?.filter(i => {
      return i.grey ? i.grey?.((window as any).loginInfo) : true;
    });

    return (
      <div style={{ display: 'flex' }}>
        {realItems.map(i => {
          const titleTxt = i?.showGreyTitle?.((window as any).loginInfo) 
            ? i.greyTitle 
            : i.title;

          return (
            <div key={i.key} className={styles.pop_menu}>
              {i.link ? (
                <Text 
                  className={styles.menu_2} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentTopKey(father.key);
                    onTopClick(i, items);
                    return false;
                  }}
                >
                  {titleTxt}
                </Text>
              ) : (
                <Text className={styles.menu_2}>
                  {i.icon?.()}<span style={{ marginLeft: 4 }}>{titleTxt}</span>
                </Text>
              )}
              {i.children && i.children?.filter(child => !child.hidden).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 20 }}>
                  {i.children?.filter(child => !child.hidden)
                    .map(j => (
                      <Text 
                        key={j.key}
                        className={styles.menu_3} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentTopKey(father.key);
                          onTopClick(j, items);
                          return false;
                        }}
                      >
                        {j.isNew && (
                          <img 
                            src='//img.alicdn.com/imgextra/i2/O1CN01aDVAxb25TsbMZ79Yg_!!6000000007528-55-tps-32-32.svg' 
                            style={{ marginLeft: -24, width: 24, marginTop: -2 }} 
                            alt="New"
                          />
                        )}
                        {j.title}
                        {j.isBlank && <ExportOutlined style={{ marginLeft: 4 }} />}
                      </Text>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [onTopClick]);

  // 跳转到第一个子菜单
  const jumpFirstSub = useCallback((curItem: MenuItem) => {
    if (curItem?.children?.length) {
      const firstLink = curItem.children[0]?.link;
      if (firstLink) {
        setCurrentTopKey(curItem.key);
        onTopClick(curItem.children[0], curItem.children || []);
      } else {
        for (const child of curItem.children) {
          const visibleChildren = child.children?.filter(i => !i.isBlank) || [];
          if (visibleChildren?.length) {
            const firstChild = visibleChildren[0];
            if (firstChild.link) {
              setCurrentTopKey(curItem.key);
              onTopClick(firstChild, curItem.children || []);
              break;
            }
          }
        }
      }
    }
  }, [onTopClick]);

  // 渲染方法
  const renderLoginErrorModal = () => (
    <Modal 
      open={visible} 
      footer={null} 
      onCancel={() => setVisible(false)} 
      closable 
      className='crmLoginErrorPop' 
      centered
    >
      <div className={styles.subpop}>
        <div className={styles.subpoptxt}>
          <InfoCircleOutlined />&nbsp;&nbsp;该账号为子账号，没有相关权限，请使用代理商账号登录
        </div>
      </div>
    </Modal>
  );

  const renderHeader = () => (
    <Header className={styles.headerWrapper}>
      <div className={styles.header}>
        <div className={styles.header_inner}>
          <img 
            src='https://img.alicdn.com/imgextra/i4/O1CN01rOTz6B1bBcMJzwH1E_!!6000000003427-55-tps-200-62.svg' 
            width='80' 
            style={{ marginTop: 13 }} 
            alt="Logo"
          />
          <Divider type="vertical" style={{ marginTop: 19 }} />
          <Text style={{ fontWeight: 600, marginTop: 16, fontSize: 20 }}>CRM</Text>
        </div>
        <Menu
          theme="light"
          mode="horizontal"
          style={{ border: "0", background: 'none', flex: 1, height: 53, lineHeight: '53px' }}
          selectedKeys={[currentTopKey]}
          id='J_CrmTopMenu'
        >
          {menus.map((item) => (
            item.children && item.children.length > 0 ? (
              <Menu.Item
                key={item.key}
                onClick={() => jumpFirstSub(item)}
              >
                <Popover
                  arrow={false}
                  content={() => (
                    <div style={{ background: '#fff' }}>
                      {renderMenuOnPop(item.children || [], item)}
                    </div>
                  )}
                >
                  <div>
                    <span style={{ fontSize: 16, fontWeight: 500 }}>{item.title}</span>
                    <CaretDownOutlined style={{ marginLeft: 4, fontSize: 12, color: '#bfbfbf' }} />
                  </div>
                </Popover>
              </Menu.Item>
            ) : (
              <Menu.Item
                key={item.key}
                onClick={() => {
                  setCurrentTopKey(item.key);
                  topMenuClick(item);
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 500 }}>{item.title}</span>
              </Menu.Item>
            )
          ))}
        </Menu>
        <LoginMod loginInfo={account} />
      </div>
      <GlobalAlert style={{marginBottom:'-8px'}}/>
    </Header>
  );

  // 主渲染
  return (
    <StyleProvider hashPriority="high" transformers={[legacyLogicalPropertiesTransformer]}>
      <ConfigProvider
        theme={{
          hashed: false,
          components: {
            Layout: {
              headerBg: 'transparent'
            },
          },
        }}
      >
        {[HOME_PATH, REGISTER_PATH].includes(cur_path) ? (
          <div>
            <Layout style={{ background: 'transparent' }}>
              <Content>
                <div id={containorId}></div>
              </Content>
            </Layout>
            {renderLoginErrorModal()}
          </div>
        ) : (
          <div id='dspV3'>
            <Layout className={styles.ct} style={{ background: 'none' }}>
              {renderHeader()}
              <Layout style={{ padding: 16, background: 'transparent', minWidth: 1408 }}>
                {realSideMenu && realSideMenu.length > 0 && (
                  <Sider collapsedWidth={0} width={180} className={styles.sidebar}>
                    <Menu
                      className='crm-sidemenu'
                      inlineIndent={6}
                      items={realSideMenu}
                      mode="inline"
                      onOpenChange={(openKeys) => {
                        setSideOpenKeys(openKeys);
                      }}
                      openKeys={sideOpenKeys}
                      selectedKeys={[currentkey]}
                      style={{ background: '#DFEAFB', borderRadius: 8, overflow: 'hidden', border: 'none' }}
                      onClick={({ key }) => {
                        // 先尝试在一级菜单中查找
                        let menuItem = realSideMenu.find(item => item.key === key)?.data;
                        
                        // 如果不是一级菜单，则在二级菜单中查找
                        if (!menuItem) {
                          menuItem = realSideMenu.find(item => 
                            item.children?.some(child => child.key === key)
                          )?.children?.find(child => child.key === key)?.data;
                        }
                        
                        if (menuItem) {
                          leftMenuClick(menuItem);
                        }
                      }}
                    />
                  </Sider>
                )}
                <Layout style={{ background: 'transparent' }}>
                  <Content
                    className={styles.content}
                    style={location.pathname === INDEX_PATH ? { padding: 0 } : { padding: '0px 24px' }}
                  >
                    <div id={containorId}></div>
                  </Content>
                </Layout>
              </Layout>
            </Layout>
          </div>
        )}
      </ConfigProvider>
    </StyleProvider>
  );
};

export default AdsLayout;